import clsx from "clsx";
import {
  BuildingLibraryIcon,
  CircleStackIcon,
  DocumentCheckIcon,
  EyeIcon,
  KeyIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

import { Container } from "../Container";

const safeguards = [
  {
    name: "Encrypted by default",
    description:
      "Personal and account data is protected with 256-bit encryption, both in transit and at rest.",
    icon: LockClosedIcon,
  },
  {
    name: "Two-factor authentication",
    description:
      "Secure every login and withdrawal with a second layer of verification, on any device.",
    icon: KeyIcon,
  },
  {
    name: "Segregated client funds",
    description:
      "Client balances are held separately from company operating accounts at all times.",
    icon: BuildingLibraryIcon,
  },
  {
    name: "Cold storage",
    description:
      "The majority of digital assets are kept offline, out of reach of online threats.",
    icon: CircleStackIcon,
  },
  {
    name: "24/7 monitoring",
    description:
      "Our systems are watched around the clock so unusual activity is flagged and stopped early.",
    icon: EyeIcon,
  },
  {
    name: "Verified accounts",
    description:
      "Identity checks keep the platform safe and help prevent fraud for every user.",
    icon: DocumentCheckIcon,
  },
];

// Dashed dividers between cells: 1 column on mobile, 2 on sm, 3 on lg
function cellBorders(index) {
  return clsx(
    index === 0 && "border-t-0",
    index === 1 && "sm:border-t-0",
    index === 2 && "lg:border-t-0",
    index % 2 === 0 ? "sm:border-r" : "sm:border-r-0",
    index % 3 === 2 ? "lg:border-r-0" : "lg:border-r"
  );
}

export function SecurityOne() {
  return (
    <section
      id="security"
      aria-labelledby="security-title"
      className="py-24 sm:py-32"
    >
      <Container>
        <div className="relative border-y border-slate-200">
          <div
            aria-hidden="true"
            className="absolute inset-y-0 left-0 border-l border-dashed border-slate-300"
          />
          <div
            aria-hidden="true"
            className="absolute inset-y-0 right-0 border-r border-dashed border-slate-300"
          />

          <div className="border-b border-slate-200 px-6 py-12 sm:px-8 lg:py-16">
            <h2
              id="security-title"
              className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
            >
              Security you don&rsquo;t have to think about.
            </h2>
            <p className="mt-4 max-w-2xl text-[18px] tracking-tight text-slate-700">
              Protecting your assets is the foundation of everything we build,
              so you can focus on your next move.
            </p>
          </div>

          <dl className="grid grid-cols-1 bg-slate-50 sm:grid-cols-2 lg:grid-cols-3">
            {safeguards.map((item, index) => (
              <div
                key={item.name}
                className={clsx(
                  "border-t border-dashed border-slate-300 px-6 py-10 sm:px-8 lg:pb-16",
                  cellBorders(index)
                )}
              >
                <item.icon
                  className="h-6 w-6 text-[#0C6CF2]"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                <dt className="mt-6 text-[18px] font-medium text-slate-900">
                  {item.name}
                </dt>
                <dd className="mt-3 text-[16px] leading-7 text-slate-600">
                  {item.description}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Container>
    </section>
  );
}
